import { useState, useRef, useMemo, ComponentType } from "react";
import { CSSTransition } from "react-transition-group";
import useOutsideClick from "@hooks/useOutsideClick";
import { Portal } from '@components/Portal';
import cn from "classnames";
import styles from "./Dropdown.module.scss";

type ValueDefaultType = string | undefined

interface SelectedOption<V> {
  id: string | symbol | number;
  value?: V;
}

export interface DropdownOptionProps<T, V = ValueDefaultType> {
  id: T;
  value?: V;
  isActive?: boolean;
}

export interface DropdownValueProps<T, V = ValueDefaultType> {
  id: T;
  value: SelectedOption<V>;
  isOpen: boolean;
  disabled?: boolean;
}

interface DropdownProps<T, V> {
  className?: string;
  options: DropdownOptionProps<T, V>[];
  defaultOption: DropdownOptionProps<T, V>;
  onChange?: (id: T, value?: V) => void;
  ValueComponent?: ComponentType<DropdownValueProps<T, V>>;
  OptionComponent?: ComponentType<DropdownOptionProps<T, V>>;
  menuAlign?: "top" | "bottom" | "left" | "right";
  menuPosition?: "top" | "bottom" | "left" | "right";
  menuWidth?: string;
  disabled?: boolean;
  footer?: React.ReactNode;
  fixed?: boolean;
}

function Dropdown<T extends string | symbol | number = string, V = ValueDefaultType>({
  className,
  defaultOption,
  options,
  onChange,
  ValueComponent,
  OptionComponent,
  menuAlign,
  menuPosition,
  menuWidth,
  disabled,
  footer,
  fixed,
}: DropdownProps<T, V>) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DropdownOptionProps<T, V>>(defaultOption);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const position = menuPosition || "bottom";
  const align = menuAlign ?? ((position === "bottom" || position === "top") ? "right" : "top");

  useOutsideClick<HTMLDivElement>(dropdownMenuRef, () => setIsOpen(false));

  const handleOptionClick = (option: DropdownOptionProps<T, V>) => {
    setIsOpen(false); // Закрываем дропдаун после выбора
    setSelectedOption(option);
    onChange && onChange(option.id, option.value);
  };

  const positionObj = useMemo(() => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current && triggerRef.current.getBoundingClientRect();
    const menuRect = dropdownMenuRef.current && dropdownMenuRef.current.getBoundingClientRect();
    const menuHeight = menuRect?.height ?? 0;
    const bottom = document.body.clientHeight - rect.bottom;

    const W = document.documentElement.clientWidth;
    const H = document.documentElement.clientHeight;

    if (position === "right") {
      return {
        ...((align === "top" || bottom < menuHeight) ? {
          bottom: `calc(${H}px - ${rect.bottom}px - ${window.scrollY}px)`,
        } : {
          top: `calc(${rect.top}px + ${window.scrollY}px)`,
        }),
        left: `calc(${styles.margin} + ${rect.right}px)`,
      };
    }
    if (position === "left") {
      return {
        ...((align === "top" || bottom < menuHeight) ? {
          bottom: `calc(${H}px - ${rect.bottom}px - ${window.scrollY}px)`,
        } : {
          top: `calc(${rect.top}px + ${window.scrollY}px)`,
        }),
        right: `calc(${W}px - ${window.scrollX}px - ${rect.left}px + ${styles.margin})`
      };
    }
    if (position === "bottom") {
      return {
        top: `calc(${rect.bottom}px + ${window.scrollY}px + ${styles.margin})`,
        ...((align === "left") ? {
          left: `calc(${window.scrollX}px + ${rect.left}px)`
        } : {
          right: `calc(${W}px - ${window.scrollX}px - ${rect.right}px)`
        }),
      };
    }
    if (position === "top") {
      return {
        bottom: `calc(${H}px - ${rect.top}px - ${window.scrollY}px + ${styles.margin})`,
        ...((align === "left") ? {
          left: `calc(${window.scrollX}px + ${rect.left}px)`
        } : {
          right: `calc(${W}px - ${window.scrollX}px - ${rect.right}px)`
        }),
      };
    }
  }, [dropdownMenuRef.current, triggerRef.current, isOpen])

  const fromModal = triggerRef?.current && triggerRef.current.closest(".modal");

  return (
    <div className={cn(styles.dropdown, className)}>
      <div
        ref={triggerRef}
        className={cn(styles.selected, disabled && styles.disabled)}
        onClick={() => (!disabled && setIsOpen(!isOpen))}
      >
        {ValueComponent ? (
          // @ts-ignore: todo
          <ValueComponent
            {...selectedOption}
            value={selectedOption}
            isOpen={isOpen}
            disabled={disabled}
          />
        ) : (<>{selectedOption?.value}</>)}
      </div>
      <Portal>
        <CSSTransition
          in={isOpen}
          timeout={300}
          classNames={{
            enter: styles.menuEnter,
            enterActive: styles.menuEnterActive,
            exit: styles.menuExit,
            exitActive: styles.menuExitActive,
          }}
          mountOnEnter
          unmountOnExit
          nodeRef={dropdownMenuRef}
        >
          <div
            className={cn(
              styles.menu,
              styles[`position-${position}`],
              fromModal && styles.fromModal,
            )}
            style={{
              ...positionObj,
              maxWidth: menuWidth,
              position: fixed ? "fixed" : "absolute",
            }}
            ref={dropdownMenuRef}
          >
            {options.map((option) => (
              OptionComponent ? (
                <div
                  key={String(option.id)}
                  onClick={() => handleOptionClick(option)}
                  className={styles.itemContainer}
                >
                  <OptionComponent {...option} isActive={option.id === selectedOption.id} />
                </div>
              ) : (
                <div
                  className={styles.item}
                  key={String(option.id)}
                  onClick={() => handleOptionClick(option)}
                >
                  <>{option?.value}</>
                </div>
              )
            ))}
            {footer ? footer : null}
          </div>
        </CSSTransition>
      </Portal>
    </div>
  );
};

export default Dropdown;
